// ============================================================
// Navigation.js
// Exports two components:
//   Header     — fixed top-left logo + timed greeting pill
//   SideNavbar — desktop side nav (right) + mobile bottom nav
//
// Resume button lives in App.js (bottom-left FAB), mirrored
// alongside the theme toggle (bottom-right FAB).
// To change resume file → update Personal.resume in personal.json
// or swap /Jobin_resume.pdf in /public/
// ============================================================

import React, { useState, useEffect } from 'react';
import {
  Fade,
  Box,
  makeStyles,
  useTheme,
  Typography,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { GetApp } from '@material-ui/icons';

import Personal from '../data/personal.json';
import darkLogo  from '../assets/images/logo-dark.webp';
import lightLogo from '../assets/images/logo-light.webp';

// ============================================================
// STYLES
// ============================================================
const useStyles = makeStyles((theme) => ({

  // ── Logo (fixed top-left) ──────────────────────────────────
  logoLink: {
    position:       'fixed',
    top:            theme.spacing(3),
    left:           theme.spacing(3),
    zIndex:         1100,
    padding:        theme.spacing(0.75, 1.5),
    borderRadius:   12,
    textDecoration: 'none',
    backdropFilter: 'blur(10px)',
    transition:     'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
    },
    [theme.breakpoints.down('sm')]: {
      top:     theme.spacing(2),
      left:    theme.spacing(2),
      padding: theme.spacing(0.5, 1),
    },
  },
  logoImage: {
    maxWidth: 165,
    height:   'auto',
    [theme.breakpoints.down('sm')]: { maxWidth: 130 },
  },

  // ── Greeting pill (fixed top-center, fades after 4s) ───────
  greetingContainer: {
    position:       'fixed',
    top:            '11%',
    left:           '50%',
    transform:      'translateX(-50%)',
    zIndex:         1000,
    padding:        theme.spacing(1.2, 3.5),
    background: theme.palette.type === 'dark'
      ? 'rgba(99, 102, 241, 0.12)'
      : 'rgba(79, 70, 229, 0.1)',
    borderRadius:   50,
    backdropFilter: 'blur(12px)',
    border:         `1px solid ${theme.palette.primary.main}30`,
    boxShadow:      '0 4px 20px rgba(0,0,0,0.15)',
  },
  greetingText: {
    fontFamily: '"Space Mono", monospace',
    fontSize:   '0.97rem',
    color:      theme.palette.primary.main,
    fontWeight: 500,
  },

  // ── Desktop side nav (right-center) ────────────────────────
  navContainer: {
    position:  'fixed',
    right:     '24px',
    top:       '50%',
    transform: 'translateY(-50%)',
    zIndex:    1000,
    [theme.breakpoints.down('sm')]: { display: 'none' },
  },
  glassBox: {
    background: theme.palette.type === 'dark'
      ? 'rgba(17,17,17,0.85)'
      : 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    borderRadius:   20,
    border:         `1px solid ${theme.palette.divider}`,
    padding:        theme.spacing(2, 1.5),
    boxShadow: theme.palette.type === 'dark'
      ? '0 15px 45px rgba(0,0,0,0.6)'
      : '0 15px 45px rgba(0,0,0,0.12)',
  },
  navItem: {
    display:        'flex',
    alignItems:     'center',
    gap:            theme.spacing(1.5),
    padding:        theme.spacing(1, 2.5),
    color:          theme.palette.text.secondary,
    textDecoration: 'none',
    borderRadius:   10,
    transition:     'all 0.25s ease',
    '&:hover': {
      color:      theme.palette.primary.main,
      background: `${theme.palette.primary.main}15`,
    },
  },
  navItemActive: {
    color:      theme.palette.primary.main,
    background: `${theme.palette.primary.main}20`,
    fontWeight: 600,
  },
  navLabel: {
    fontFamily:    '"Space Mono", monospace',
    fontSize:      '0.7rem',
    letterSpacing: '0.1em',
  },

  // ── Mobile bottom nav ──────────────────────────────────────
  mobileNav: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display:        'flex',
      position:       'fixed',
      bottom:         0,
      left:           0,
      right:          0,
      zIndex:         1100,
      background: theme.palette.type === 'dark'
        ? 'rgba(10,10,15,0.95)'
        : 'rgba(255, 255, 255, 0.82)',
      backdropFilter: 'blur(10px)',
      borderTop:      `1px solid ${theme.palette.divider}`,
      padding:        theme.spacing(1.2, 0),
    },
  },
  mobileNavItem: {
    flex:           1,
    textAlign:      'center',
    color:          theme.palette.text.secondary,
    textDecoration: 'none',
    padding:        theme.spacing(0.5, 0),
    '&.active': { color: theme.palette.primary.main },
  },
  mobileNavLabel: {
    fontFamily:    '"Space Mono", monospace',
    fontSize:      '0.68rem',
    letterSpacing: '0.05em',
  },

  // ── Resume FAB (bottom-left, mirrors theme toggle bottom-right) ──
  // Positioned to sit directly above the mobile nav on small screens.
  // On desktop it floats freely at bottom-left.
  resumeBtn: {
    position:       'fixed',
    bottom:         50,
    left:           20,
    zIndex:         1200,
    background:     theme.palette.background.paper,
    backdropFilter: 'blur(12px)',
    // Subtle accent border so it reads as a distinct action from the theme toggle
    border: `1px solid ${theme.palette.primary.main}40`,
    transition:     'all 0.25s ease',
    '&:hover': {
      background:  theme.palette.primary.main,
      color:       '#ffffff',
      borderColor: 'transparent',
      transform:   'translateY(-2px)',
      boxShadow:   `0 8px 24px ${theme.palette.primary.main}55`,
    },
    // On mobile, lift above the bottom nav bar (64px tall approx)
    [theme.breakpoints.down('sm')]: {
      bottom: 50,
    },
  },
}));

// ============================================================
// NAV CONFIG
// Add/remove sections here — no other changes needed.
// ============================================================
const NAV_ITEMS = [
  { id: 'home',         label: 'HOME'  },
  { id: 'works',        label: 'WORK'  },
  { id: 'certificates', label: 'CERTS' },
  { id: 'about',        label: 'ABOUT' },
];

// ── Greeting helper — reads local time ────────────────────────
// To customise strings → add greetings:{morning,afternoon,evening}
// to personal.json
const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return Personal.greetings?.morning   || 'Good morning';
  if (h >= 12 && h < 17) return Personal.greetings?.afternoon || 'Good afternoon';
  return                         Personal.greetings?.evening   || 'Good evening';
};

// ============================================================
// HEADER — logo + greeting
// ============================================================
export const Header = () => {
  const classes      = useStyles();
  const theme        = useTheme();
  const [showGreeting, setShowGreeting] = useState(true);

  // Hide greeting pill after 4s
  useEffect(() => {
    const t = setTimeout(() => setShowGreeting(false), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* Logo — links back to hero */}
      <a href="#home" className={classes.logoLink}>
        <img
          src={theme.palette.type === 'dark' ? darkLogo : lightLogo}
          alt={Personal.name}
          className={classes.logoImage}
        />
      </a>

      {/* Greeting pill — fades out after 4s */}
      <Fade in={showGreeting} timeout={800}>
        <Box className={classes.greetingContainer}>
          <Typography className={classes.greetingText}>
            {getGreeting()} 
          </Typography>
        </Box>
      </Fade>
    </>
  );
};

// ============================================================
// SIDE NAVBAR — desktop pill + mobile bottom bar + resume FAB
// ============================================================
export const SideNavbar = () => {
  const classes = useStyles();
  const [active, setActive] = useState('home');

  // Highlight active section via IntersectionObserver —
  // no scroll listener needed, so no jank.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let visible = null;
        entries.forEach((e) => { if (e.isIntersecting) visible = e.target.id; });
        if (!visible) return;
        setActive(visible);
        if (window.history.replaceState)
          window.history.replaceState(null, '', `#${visible}`);
      },
      { threshold: 0.6 }
    );

    NAV_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(id);
    if (window.history.replaceState)
      window.history.replaceState(null, '', `#${id}`);
  };

  // Resume path — file lives in /public/Jobin_resume.pdf
  // To update: replace that file. To rename: change the string below.
  const resumeHref = '/Jobin_resume.pdf';

  return (
    <>
      {/* ── Desktop side nav ─────────────────────────────────── */}
      <nav className={classes.navContainer}>
        <div className={classes.glassBox}>
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className={`${classes.navItem} ${active === item.id ? classes.navItemActive : ''}`}
            >
              <Typography className={classes.navLabel}>{item.label}</Typography>
            </a>
          ))}
        </div>
      </nav>

      {/* ── Mobile bottom nav ────────────────────────────────── */}
      <nav className={classes.mobileNav}>
        {NAV_ITEMS.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            onClick={(e) => handleClick(e, item.id)}
            className={`${classes.mobileNavItem} ${active === item.id ? 'active' : ''}`}
          >
            <Typography className={classes.mobileNavLabel}>{item.label}</Typography>
          </a>
        ))}
      </nav>

      {/* ── Resume FAB (bottom-left) ──────────────────────────
          Mirrors the theme toggle FAB in App.js (bottom-right).
          Opens PDF in a new tab — no forced download.
          To force download instead: add download="Jobin_Abraham_Resume" attr.
      ──────────────────────────────────────────────────────── */}
      <Tooltip title="View Resume" arrow placement="right">
        <IconButton
          className={classes.resumeBtn}
          component="a"
          href={resumeHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View Resume"
        >
          <GetApp />
        </IconButton>
      </Tooltip>
    </>
  );
};